'use client'
import {
	type DialogHTMLAttributes,
	type MouseEvent,
	useEffect,
	useRef
} from 'react'

import { useDialogPolyfill } from './polyfill'

export type ModalProps = Pick<
	DialogHTMLAttributes<HTMLDialogElement>,
	'aria-labelledby' | 'children' | 'className' | 'open'
> & {
	onRequestClose: () => unknown
	rootClassName?: string
}

export function ControlledDialog({
	children,
	className,
	onRequestClose,
	open = false,
	rootClassName,
	...props
}: ModalProps) {
	const dialogRef = useRef<HTMLDialogElement>(null)
	useDialogPolyfill(dialogRef)

	/**
	 * @hint prevents calling imperative methods on mount since the polyfill will throw
	 * an error since we are not using the `open` attribute
	 */
	useEffect(() => {
		const dialogNode = dialogRef.current

		open ? dialogNode?.showModal() : dialogNode?.close()
	}, [open])

	function handleOutsideClick(event: MouseEvent<HTMLDialogElement>) {
		if (event.target === dialogRef.current) {
			onRequestClose()
		}
	}

	return (
		<dialog
			className={rootClassName}
			onClick={handleOutsideClick}
			onClose={onRequestClose}
			ref={dialogRef}
			{...props}
		>
			<div className={className}>{children}</div>
		</dialog>
	)
}
